import { setUiLanguage, storeDeviceLanguage } from "$/lib/language-preference"
import {
  ExtendedUser,
  Language,
  Route,
  TranslationDictionaryResponse,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { request } from "../api"
import { publicRequest } from "../public-api"
import { userQueryOptions } from "./user-queries"

async function fetchDictionary(
  language: Language,
): Promise<TranslationDictionaryResponse> {
  return publicRequest<TranslationDictionaryResponse>({
    url: populateRoute(Route.TRANSLATION__FIND_PUBLIC, language),
    method: "GET",
  })
}

async function updateMyLanguage(language: Language): Promise<void> {
  return request<void>({
    url: Route.USER_CONTROLLER__UPDATE_MY_LANGUAGE,
    method: "PUT",
    data: { language },
  })
}

export function useUpdateMyLanguage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (language: Language) => {
      // Load the new texts before switching, so the app doesn't suspend
      await Promise.all([
        updateMyLanguage(language),
        queryClient.ensureQueryData(translationQueries.dictionary(language)),
      ])
      return language
    },
    onSuccess: (language) => {
      storeDeviceLanguage(language)
      setUiLanguage(language)
      queryClient.setQueryData<ExtendedUser>(
        userQueryOptions.queryKey,
        (user) => (user ? { ...user, language } : user),
      )
      // Cached responses hold part names in the old language
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] !== translationQueries.all()[0],
      })
    },
  })
}

const translationQueries = {
  all: () => ["translations"],
  dictionary: (language: Language) =>
    queryOptions({
      queryKey: [...translationQueries.all(), language],
      queryFn: () => fetchDictionary(language),
      // Admin edits reach open tabs within this window; new texts show at once
      // during development.
      staleTime: import.meta.env.DEV ? 0 : 5 * 60 * 1000,
    }),
}

export default translationQueries
